import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "device-selection-content" }
const _hoisted_2 = { class: "terms-text" }
const _hoisted_3 = { class: "button-container" }
const _hoisted_4 = { class: "qr-popover-content" }
const _hoisted_5 = { class: "qr-code-box" }
const _hoisted_6 = ["src"]

import { ref, watch, onMounted } from "vue";
import { UserTracking } from "@/utils/UserTracking";

const termsUrl = "https://cdn.feegow.com/terms/Termos-e-Condicoes-Gerais-de-Uso-Noa-BR-PT.pdf";

// =============================================================================
// Props & Emits
// =============================================================================

interface Props {
  isOpen: boolean;
  sessionData: {
    recorderUrl?: string;
    qrCode?: string;
    [key: string]: unknown;
  } | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AIAssistantDeviceSelectionModal',
  props: {
    isOpen: { type: Boolean },
    sessionData: {}
  },
  emits: ["close", "select-desktop", "select-mobile"],
  setup(__props: any, { emit: __emit }) {

/**
 * AIAssistantDeviceSelectionModal.vue - Device Selection Modal
 * 
 * Standalone modal for selecting recording device (desktop or mobile).
 * Shown after template selection, before the recorder modal opens.
 */
const debugLog = (location: string, message: string, data?: Record<string, unknown>) => {
  console.log(`[DeviceSelectionModal:${location}] ${message}`, data || "");
};

const props = __props;

const emit = __emit;

// =============================================================================
// State
// =============================================================================

const transcriptionQrCode = ref<string | null>(null);
const transcriptionUrl = ref<string | null>(null);
const qrCodeState = ref<"pending" | "error" | "created">("pending");

// =============================================================================
// Watchers
// =============================================================================

watch(
  () => props.sessionData,
  (newData) => {
    if (newData) {
      debugLog("watch:sessionData", "Session data received", {
        hasRecorderUrl: !!newData.recorderUrl,
        hasQrCode: !!newData.qrCode,
      });
      
      transcriptionUrl.value = newData.recorderUrl || null;
      transcriptionQrCode.value = newData.qrCode || null;
      qrCodeState.value = newData.qrCode ? "created" : "pending";
    }
  },
  { immediate: true }
);

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      debugLog("watch:isOpen", "Modal opened", {
        hasSessionData: !!props.sessionData,
        hasRecorderUrl: !!props.sessionData?.recorderUrl,
      });
    }
  }
);

// =============================================================================
// Event Handlers
// =============================================================================

const onClickDesktop = () => {
  debugLog("onClickDesktop", "User selected desktop recording", {
    hasUrl: !!transcriptionUrl.value,
  });
  
  UserTracking.trackEvent("noa_notes_recording_device_selected", {
    action: "initiate noa notes record",
    device_type: "desktop",
    noa_notes_client: "feegow",
    page_category: "patients",
    page_section: "patient profile",
    page_type: "",
  });

  if (transcriptionUrl.value) {
    emit("select-desktop", transcriptionUrl.value);
  }
};

const onClickMobile = () => {
  debugLog("onClickMobile", "User selected mobile recording - QR popover shown");
  
  UserTracking.trackEvent("noa_notes_recording_device_selected", {
    action: "initiate noa notes record",
    device_type: "mobile",
    noa_notes_client: "feegow",
    page_category: "patients",
    page_section: "patient profile",
    page_type: "",
  });

  // QR code popover is handled by w-popover component automatically
  emit("select-mobile");
};

// =============================================================================
// Lifecycle
// =============================================================================

onMounted(() => {
  debugLog("onMounted", "Component mounted", {
    isOpen: props.isOpen,
    hasSessionData: !!props.sessionData,
  });
});

return (_ctx: any,_cache: any) => {
  const _component_w_text = _resolveComponent("w-text")!
  const _component_w_spacer = _resolveComponent("w-spacer")!
  const _component_w_link = _resolveComponent("w-link")!
  const _component_w_stack = _resolveComponent("w-stack")!
  const _component_w_button = _resolveComponent("w-button")!
  const _component_w_spinner = _resolveComponent("w-spinner")!
  const _component_w_icon = _resolveComponent("w-icon")!
  const _component_w_popover = _resolveComponent("w-popover")!
  const _component_w_modal = _resolveComponent("w-modal")!

  return (_openBlock(), _createBlock(_component_w_modal, {
    "model-value": __props.isOpen,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = (val) => !val && emit('close')),
    size: "small",
    class: "device-selection-modal",
    title: _ctx.$t('deviceSelection.title', 'Iniciar nova gravação Noa Notes'),
    "close-on-click-outside": false,
    style: {"z-index":"999999999"},
    onClose: _cache[1] || (_cache[1] = ($event: any) => (emit('close')))
  }, {
    body: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_w_stack, {
          class: "welcome-container",
          gap: "small",
          direction: "column"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_w_text, { type: "body" }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('deviceSelection.subtitle', 'Noa Notes é uma transcrição de IA, use-a para automatizar todas as suas anotações durante a visita.')), 1)
              ]),
              _: 1
            }),
            _createVNode(_component_w_spacer, { size: "m" }),
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_w_text, {
                type: "caption",
                class: "muted-text"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('deviceSelection.termsTextStart', 'Ao iniciar a transcrição, você concorda com nossos')) + " ", 1),
                  _createVNode(_component_w_link, {
                    target: "_blank",
                    href: termsUrl
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('deviceSelection.termsTextLinkText', 'Termos e Condições')), 1)
                    ]),
                    _: 1
                  }),
                  _createTextVNode(". " + _toDisplayString(_ctx.$t('deviceSelection.termsTextEnd', 'Peça permissão ao seu paciente antes de começar a transcrever o áudio.')), 1)
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        })
      ])
    ]),
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_w_popover, {
          position: "top",
          class: "qr-popover",
          style: {"z-index":"9999999999"}
        }, {
          content: _withCtx(() => [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_component_w_text, {
                type: "caption",
                "text-align": "center"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('deviceSelection.scanQrCode', 'Escaneie o código QR para gravar no celular')), 1)
                ]),
                _: 1
              }),
              _createVNode(_component_w_spacer, { size: "s" }),
              _createElementVNode("div", _hoisted_5, [
                (!transcriptionQrCode.value && qrCodeState.value === 'pending')
                  ? (_openBlock(), _createBlock(_component_w_spinner, {
                      key: 0,
                      size: "medium"
                    }))
                  : (qrCodeState.value === 'error')
                    ? (_openBlock(), _createBlock(_component_w_icon, {
                        key: 1,
                        name: "warning",
                        color: "error",
                        size: "medium"
                      }))
                    : (transcriptionQrCode.value)
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 2,
                          src: transcriptionQrCode.value,
                          class: "qr-code-img",
                          alt: "QR Code",
                          "data-dd-privacy": "hidden"
                        }, null, 8, _hoisted_6))
                      : _createCommentVNode("", true)
              ])
            ])
          ]),
          default: _withCtx(() => [
            _createVNode(_component_w_button, {
              class: "device-button",
              label: _ctx.$t('deviceSelection.continueOnMobile', 'Continuar no celular'),
              variant: "secondary",
              "leading-icon": "mobile-phone",
              onClick: onClickMobile,
              loading: !transcriptionUrl.value,
              disabled: !transcriptionUrl.value
            }, null, 8, ["label", "loading", "disabled"])
          ]),
          _: 1
        }),
        _createVNode(_component_w_button, {
          class: "device-button",
          onClick: onClickDesktop,
          label: _ctx.$t('deviceSelection.startRecording', 'Iniciar gravação'),
          variant: "primary",
          "trailing-icon": "arrow-right",
          loading: !transcriptionUrl.value,
          disabled: !transcriptionUrl.value
        }, null, 8, ["label", "loading", "disabled"])
      ])
    ]),
    _: 1
  }, 8, ["model-value", "title"]))
}
}

})